/**
 * Centralized OG/share host resolver with auto-fallback.
 *
 * Primary: assets.gameon.futbol (Supabase custom domain → Functions)
 * Fallback: direct *.supabase.co Functions URL
 *
 * If the branded host fails a runtime probe, we transparently swap in the
 * direct Supabase URL for the rest of the session. Recipients still see a
 * rich OG card — only the hostname in the URL preview differs.
 *
 * The probe runs at most once per 5 minutes per tab, cached in sessionStorage.
 */

const BRANDED_HOST = "https://assets.gameon.futbol";
const DIRECT_HOST = "https://etrspsphgbkapczqxboy.supabase.co";

const CACHE_KEY = "og-host-health-v1";
const TTL_MS = 5 * 60 * 1000;
const PROBE_TIMEOUT_MS = 2000;

type Mode = "branded" | "direct";
type CachedHealth = { mode: Mode; ts: number };

function readCache(): CachedHealth | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedHealth;
    if (!parsed || (parsed.mode !== "branded" && parsed.mode !== "direct")) return null;
    if (Date.now() - parsed.ts > TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(mode: Mode) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ mode, ts: Date.now() } satisfies CachedHealth),
    );
    window.dispatchEvent(new CustomEvent("og-host-health-changed", { detail: { mode } }));
  } catch {
    // sessionStorage may be disabled (privacy mode, SSR, etc.) — best effort only.
  }
}

let inFlightProbe: Promise<Mode> | null = null;

/**
 * Probe the branded host. Returns the mode to use.
 * - 200/2xx/3xx → 'branded'
 * - timeout / 4xx / 5xx / network error → 'direct'
 *
 * Uses a tiny GET against `og-rsvp` with no id (we just need to see if Supabase
 * routes the request — even a 400 from the function counts as healthy because
 * it proves the hostname maps to our Functions service).
 */
async function probe(): Promise<Mode> {
  const probeUrl = `${BRANDED_HOST}/functions/v1/og-rsvp?probe=1`;
  try {
    const res = await fetch(probeUrl, {
      method: "GET",
      cache: "no-store",
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    // 404 from Cloudflare (no sb-* header) means the hostname isn't routed.
    // Anything served by Supabase's edge — even a 400 — proves routing works.
    const isSupabaseEdge =
      res.headers.get("sb-edge-region") !== null ||
      res.headers.get("sb-gateway-version") !== null ||
      res.headers.get("x-deno-execution-id") !== null ||
      (res.status >= 200 && res.status < 500 && res.status !== 404);
    return isSupabaseEdge ? "branded" : "direct";
  } catch {
    return "direct";
  }
}

/**
 * Resolve the OG host. Synchronous read of cache; kicks off an async probe
 * on first call (per session window) but always returns immediately so share
 * actions never block.
 */
export function getOgHost(): string {
  const cached = readCache();
  if (cached) {
    return cached.mode === "branded" ? BRANDED_HOST : DIRECT_HOST;
  }
  // No cache — assume branded (the happy path), kick off a probe for next call.
  if (typeof window !== "undefined" && !inFlightProbe) {
    inFlightProbe = probe()
      .then((mode) => {
        writeCache(mode);
        return mode;
      })
      .finally(() => {
        inFlightProbe = null;
      });
  }
  return BRANDED_HOST;
}

/** True when we've confirmed the branded host is broken this session. */
export function isFallbackActive(): boolean {
  return readCache()?.mode === "direct";
}

/** Force a re-probe (e.g. on tab refocus or manual retry). */
export async function refreshOgHostHealth(): Promise<Mode> {
  if (inFlightProbe) return inFlightProbe;
  inFlightProbe = probe()
    .then((mode) => {
      writeCache(mode);
      return mode;
    })
    .finally(() => {
      inFlightProbe = null;
    });
  return inFlightProbe;
}

export function ogGameUrl(gameId: string): string {
  return `${getOgHost()}/functions/v1/og-game?id=${gameId}`;
}

export function ogRsvpUrl(gameId: string): string {
  return `${getOgHost()}/functions/v1/og-rsvp?id=${gameId}`;
}

/**
 * Rewrites og-* proxy links inside a share message to their direct
 * vote.gameon.futbol equivalents.
 *
 * Use this for the native share sheet / clipboard paths, where no OG card is
 * generated by us anyway — a same-host link avoids the cross-host redirect that
 * Android in-app WebViews hand to the intent resolver ("requires Chrome").
 * Keep the proxy URL for messenger sends where the rich preview matters.
 */
const SITE_ORIGIN = "https://vote.gameon.futbol";

export function toDirectShareUrl(url: string): string {
  try {
    const u = new URL(url);
    if (!/\/functions\/v1\/og-(rsvp|game|attendance|rpe|autopilot)$/.test(u.pathname)) {
      return url;
    }
    const fn = u.pathname.split("/").pop() as string;
    const id = u.searchParams.get("id");
    const team = u.searchParams.get("team");
    const session = u.searchParams.get("session");

    let path: string | null = null;
    if (fn === "og-rsvp" && id) path = `/rsvp/${id}`;
    else if (fn === "og-game" && id) path = `/game/${id}`;
    else if (fn === "og-attendance") path = team ? `/teams/${team}/attendance` : "/teams";
    else if (fn === "og-rpe") {
      if (session) path = `/rpe/session/${session}`;
      else if (team) path = `/teams/${team}/rpe`;
    } else if (fn === "og-autopilot") path = "/auto-sync";
    if (!path) return url;

    const extra = new URLSearchParams();
    u.searchParams.forEach((v, k) => {
      if (k !== "id" && k !== "team" && k !== "session") extra.append(k, v);
    });
    const qs = extra.toString();
    return `${SITE_ORIGIN}${path}${qs ? `?${qs}` : ""}`;
  } catch {
    return url;
  }
}

/** Rewrites every og-* proxy URL found in a free-text share message. */
export function withDirectLinks(text: string): string {
  return text.replace(/https:\/\/\S*\/functions\/v1\/og-[a-z]+\?\S*/gi, (match) => {
    const cleaned = match.replace(/[.,)\]]+$/, "");
    const trailing = match.slice(cleaned.length);
    return toDirectShareUrl(cleaned) + trailing;
  });
}
